import { Pressable, Text, View } from "react-native";
import { ChevronRight, Plus } from "lucide-react-native";
import {
  CompactActionButton,
  DialogIcon,
  EmptyState,
  SectionTitle,
  cardSurface,
} from "@louise/moon-ui";
import {
  formatCurrency,
  formatDate,
  type Locale,
  useTranslations,
} from "@src/packages/i18n";
import type { ApiPaymentRecord } from "@src/packages/costs/hooks/usePaymentRecords";
import { EMBER } from "../../../onboarding/theme";

const PAYMENT_KIND_LABEL_KEY = {
  payment: "costs.cost_upload.record_kinds.payment",
  claim: "costs.cost_upload.record_kinds.claim",
  receipt: "costs.cost_upload.record_kinds.receipt",
  balance: "costs.cost_upload.record_kinds.balance",
} as const satisfies Record<ApiPaymentRecord["kind"], string>;

const COST_ESTIMATE_ILLUSTRATION =
  require("../../../assets/images/cost-estimate-coins.png") as number;

type Props = {
  taxYear: number;
  onAddExpense: () => void;
  withCard?: boolean;
  /** Records from other tax years — surfaced so a newly added record is visible
   *  even when it falls outside the review year. */
  otherYearRecords?: ApiPaymentRecord[];
  onOpenRecord?: (record: ApiPaymentRecord) => void;
  locale?: Locale;
};

const OTHER_RECORDS_PREVIEW_LIMIT = 4;

function formatOccurredOn(
  occurredOn: string | null | undefined,
  locale: Locale,
) {
  if (!occurredOn) return "";
  const date = new Date(`${occurredOn}T00:00:00`);
  if (Number.isNaN(date.getTime())) return occurredOn;
  return formatDate(date, locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function TaxExpenseEmptyState({
  taxYear,
  onAddExpense,
  withCard = false,
  otherYearRecords,
  onOpenRecord,
  locale = "en",
}: Props) {
  const t = useTranslations();
  const previewRecords = (otherYearRecords ?? [])
    .slice()
    .sort((a, b) => (b.occurredOn ?? "").localeCompare(a.occurredOn ?? ""))
    .slice(0, OTHER_RECORDS_PREVIEW_LIMIT);

  return (
    <View style={withCard ? cardSurface() : undefined}>
      <EmptyState
        testID="tax-review-empty-expenses"
        density="compact"
        titleVariant="featured"
        illustration={
          <DialogIcon
            source={COST_ESTIMATE_ILLUSTRATION}
            width={68}
            height={80}
          />
        }
        title={t("costs.tax_review.your_payments_and_receipts")}
        subtitle={t("costs.tax_review.add_payments_and_receipts_for_tax_year", {
          params: { tax_year: String(taxYear) },
        })}
        action={
          <CompactActionButton
            label={t("costs.tax_review.add_expense")}
            action="costs.tax_review.add_expense"
            variant="primary"
            leadingIcon={Plus}
            onPress={onAddExpense}
          />
        }
      />
      {/* Records from other tax years, so one she has just added is visible
          even when it falls outside the year under review. */}
      {previewRecords.length > 0 ? (
        <View style={{ width: "100%", gap: 8, marginTop: 6 }}>
          <SectionTitle level="subsection">
            {t("costs.tax_review.recently_added_records")}
          </SectionTitle>
          {previewRecords.map((record) => (
            <Pressable
              key={record.id}
              onPress={() => onOpenRecord?.(record)}
              disabled={!onOpenRecord}
              style={{
                ...cardSurface({ variant: "panel" }),
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 12,
              }}
            >
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  {record.label || t(PAYMENT_KIND_LABEL_KEY[record.kind])}
                </Text>
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textLight,
                    marginTop: 2,
                  }}
                >
                  {t(PAYMENT_KIND_LABEL_KEY[record.kind])} ·{" "}
                  {formatCurrency(record.amount ?? 0, locale)}
                  {record.occurredOn
                    ? ` · ${formatOccurredOn(record.occurredOn, locale)}`
                    : ""}
                </Text>
              </View>
              {onOpenRecord ? (
                <ChevronRight
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.5}
                />
              ) : null}
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}
